import type { BenefitsSectionProps } from "~/sections/Content/BenefitsSection"
import { useId } from "react"
import Slider from "~/components/ui/Slider"
import Icon from "~/components/ui/Icon"
import SliderJS from "~/components/ui/SliderJS"
import clsx from "~/sdk/clx"
import Image from "~/components/ui/Image"

interface BenefitsSliderProps extends BenefitsSectionProps {
    renderSliderOnMobile?: boolean
    isMobile?: boolean
}

export function BenefitsSlider({ items, isMobile, renderSliderOnMobile }: BenefitsSliderProps) {
    const id = useId()

    return (
        <div
            className={clsx(
                !isMobile && 'bg-red-wine',
                isMobile && renderSliderOnMobile && 'bg-white p-2',
            )}
        >
            <div id={id} className='relative flex'>
                <Slider className='carousel scrollbar-none w-full h-12 lg:max-w-[95%] lg:mx-auto'>
                    {items.map((item, idx) => {
                        const Container = item.href ? 'a' : 'div'
                        const containerProps = item.href
                            ? { href: item.href, target: item.target ? '_blank' : '_self' }
                            : {}

                        return (
                            <Slider.Item
                                key={`${idx}-${item.href ?? item.label ?? ''}`}
                                index={idx}
                                className={clsx(
                                    'carousel-item justify-center w-full sm:w-[calc(100%/2)] lg:w-[calc(100%/3)] min-[1365px]:w-[calc(100%/4)] 2xl:!w-[calc(100%/5)]',
                                    // `scroll-mx-1` compensa a margem no ponto de snap: sem ele o
                                    // `.carousel` (snap mandatory) rola 4px antes do 1º paint para
                                    // alinhar o 1º item, e o Chrome encerra a medição de LCP — PLP e
                                    // PDP ficavam sem nenhum evento de LCP (o Lighthouse estimava 15s).
                                    isMobile && renderSliderOnMobile && 'bg-black mx-1 scroll-mx-1',
                                )}
                            >
                                <div className='relative flex items-center p-3'>
                                    <Container
                                        {...containerProps}
                                        className='flex items-center'
                                    >
                                        {item.image && (
                                            <div className='flex max-w-[25px] max-h-[25px] object-cover mr-3'>
                                                <Image
                                                    width={20}
                                                    height={20}
                                                    src={item.image}
                                                    alt={item.label}
                                                />
                                            </div>
                                        )}
                                        <div
                                            className='text-white text-[10px] lg:max-w-[280px] min-[1365px]:max-w-[300px] truncate whitespace-pre-wrap line-clamp-2 overflow-hidden [&_strong]:text-bege [&_strong]:font-black'
                                            dangerouslySetInnerHTML={{
                                                __html: item.description,
                                            }}
                                        />
                                    </Container>
                                </div>
                            </Slider.Item>
                        )
                    })}
                </Slider>

                <div className='w-full absolute top-1/2 max-w-[1920px]'>
                    <Slider.PrevButton className='absolute left-2 -translate-y-1/2 disabled:pointer-events-none disabled:opacity-0'>
                        <Icon
                            className='text-salmon'
                            size={20}
                            id='FilledChevronLeft'
                            strokeWidth={3}
                        />
                    </Slider.PrevButton>
                    <Slider.NextButton className='absolute right-2 -translate-y-1/2 disabled:pointer-events-none disabled:opacity-0'>
                        <Icon
                            className='text-salmon'
                            size={20}
                            id='FilledChevronRight'
                            strokeWidth={3}
                        />
                    </Slider.NextButton>
                </div>

                <SliderJS rootId={id} infinite />
            </div>
        </div>
    )
}
